import { useEffect, useRef } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { getDisplayHtml } from '../store/commands';
import { compileThemes } from './theme-compiler';
import { scrapeClasses, scrapeClassTokens } from './scrapeClasses';
import { makeCompiler, browserCompileFn } from './tailwind-compiler';

// The vendored shadcn `@/ui` component sources — their cva variant strings are
// the full class vocabulary the components paint with. Eager-loaded as raw text
// once; scraped into compile candidates so utilities like `bg-card` generate
// (they live in the library, never in any frame's html, so scrapeClasses misses
// them). import.meta.glob is a no-op under environments without it.
const uiKitSources = Object.values(
  import.meta.glob('../ui-kit/**/*.{ts,tsx}', { query: '?raw', import: 'default', eager: true }) as Record<string, string>,
);
const UI_KIT_CLASSES = scrapeClassTokens(uiKitSources);

/** Runtime Tailwind: collect class strings from every design frame's display
 *  HTML + tsx sources + the @/ui library + the theme registry, compile one
 *  global sheet, inject it. Debounced; skips when the input hash is unchanged. */
export function useTailwindCompile(debounceMs = 200) {
  const frames = useCanvasStore((s) => s.doc.frames);
  const themes = useCanvasStore((s) => s.doc.themes);
  const lastHash = useRef('');
  const compiler = useRef(makeCompiler(browserCompileFn));

  useEffect(() => {
    const byId = new Map(frames.map((f) => [f.id, f] as const));
    const htmls = frames
      .filter((f) => f.kind === 'free' || f.kind === 'main' || f.kind === 'instance')
      .map((f) => getDisplayHtml(f, byId));
    const classes = scrapeClasses(htmls);
    // tsx frame sources (className="…" + inline cva) + the @/ui library vocab.
    const tsxSources = frames
      .filter((f) => f.kind === 'tsxFree' || f.kind === 'tsxMain')
      .map((f) => (f as { tsx: string }).tsx);
    for (const c of scrapeClassTokens(tsxSources)) classes.add(c);
    for (const c of UI_KIT_CLASSES) classes.add(c);
    const { themeBlock, scopeBlocks } = compileThemes(themes ?? []);
    const hash = JSON.stringify([[...classes].sort(), themeBlock, scopeBlocks]);
    if (hash === lastHash.current) return;
    const id = setTimeout(() => {
      // Commit the hash only when the compile actually fires — NOT synchronously
      // up front. Otherwise StrictMode's double effect-invoke (or any re-render
      // within the debounce) clears this timeout and the second invoke sees the
      // hash already committed → early-returns → compile never runs → the
      // cf-tailwind sheet never injects (no theme/utility styles in dev).
      lastHash.current = hash;
      compiler.current
        .compile({ themeBlock, scopeBlocks, classes })
        .catch((e) => console.warn('[tailwind] compile failed', e));
    }, debounceMs);
    return () => clearTimeout(id);
  }, [frames, themes, debounceMs]);
}
